import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { HoleriteCard } from "@/components/folha/HoleriteCard";
import { AjustarItemModal, type ItemAjustavel } from "@/components/folha/AjustarItemModal";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { LancamentosCltPainel } from "@/components/folha/LancamentosCltPainel";
import { AvisoContextual } from "@/components/folha/AvisoContextual";
import { api } from "@/lib/api";
import { contemBusca } from "@/lib/busca";
import { formatCents } from "@/lib/format";
import { ROTULO_TIPO_HOLERITE, type Holerite, type HoleriteResumo } from "@/data/folha";
import type { User } from "@/data/mock";
import { toast } from "sonner";
import { AlertTriangle, Info, Loader2, Search } from "lucide-react";

interface Props {
  folhaId: string;
  holerites: HoleriteResumo[];
  editavel: boolean;
  colaboradores: User[];
  onChange: () => void;
}

/** Até R$ 1,00 de diferença para a contabilidade é arredondamento, não divergência. */
const TOLERANCIA_CENTAVOS = 100;

const diferencaContabilidade = (h: HoleriteResumo) =>
  h.liquidoContabilidade !== null ? h.liquido - h.liquidoContabilidade : null;

const divergeDaContabilidade = (h: HoleriteResumo) => {
  const d = diferencaContabilidade(h);
  return d !== null && Math.abs(d) > TOLERANCIA_CENTAVOS;
};

/**
 * Holerites CLT da competência, calculados pelo sistema, com o recibo completo
 * ao clicar na linha. Quando o extrato da contabilidade foi importado, o
 * líquido dele aparece ao lado para conferência.
 */
export const HoleritesCltPainel = ({ folhaId, holerites, editavel, colaboradores, onChange }: Props) => {
  const [busca, setBusca] = useState("");
  const [aberto, setAberto] = useState<Holerite | null>(null);
  const [carregando, setCarregando] = useState<string | null>(null);
  const [ajustando, setAjustando] = useState<(ItemAjustavel & { colaboradorId: string }) | null>(null);

  /** Mesmo modal do PJ; o ajuste fica em lançamentos CLT e entra no holerite mensal. */
  const abrirAjuste = (h: HoleriteResumo) => {
    if (!h.colaborador) return;
    setAjustando({
      id: h.id,
      colaboradorId: h.colaborador.id,
      nome: h.nome,
      liquido: h.liquido,
      ajusteManual: h.ajusteManual ?? 0,
      ajusteMotivo: h.ajusteMotivo ?? null,
      outrosDescontos: h.outrosDescontos ?? 0,
    });
  };
  const mostrarAjuste = holerites.some((h) => h.ajustavel);

  const abrir = async (h: HoleriteResumo) => {
    if (h.divergente && h.proventos === 0 && h.descontos === 0) return; // pendência sem cálculo
    setCarregando(h.id);
    try {
      setAberto(await api.get<Holerite>(`/folhas/${folhaId}/holerites/${h.id}`));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao abrir o holerite");
    } finally {
      setCarregando(null);
    }
  };

  const termo = busca.trim();
  const visiveis = termo
    ? holerites.filter((h) => contemBusca(h.nome, termo) || contemBusca(h.cargo ?? "", termo))
    : holerites;
  const soma = (f: (h: HoleriteResumo) => number) => visiveis.reduce((s, h) => s + f(h), 0);
  const calculados = holerites.some((h) => h.origem === "calculado");
  const comConferencia = holerites.some((h) => h.liquidoContabilidade !== null);
  const comPendencia = holerites.filter((h) => h.divergente).length;
  const naoBatem = holerites.filter(divergeDaContabilidade).length;

  return (
    <div className="space-y-3">
      <LancamentosCltPainel folhaId={folhaId} editavel={editavel} colaboradores={colaboradores} onChange={onChange} />

      {holerites.length === 0 ? (
        <div className="rounded-xl border bg-card py-12 text-center text-sm text-muted-foreground">
          Nenhum CLT ativo nesta competência. Cadastre o vínculo CLT, o salário e a admissão em Usuários.
        </div>
      ) : (
        <>
          {(comPendencia > 0 || naoBatem > 0) && (
            <AvisoContextual tom="erro" icon={AlertTriangle}>
              {[
                comPendencia > 0 &&
                  `${comPendencia} holerite${comPendencia === 1 ? "" : "s"} com pendência de cálculo (cadastro incompleto, erro ou líquido negativo)`,
                naoBatem > 0 &&
                  `${naoBatem} ${naoBatem === 1 ? "não bate" : "não batem"} com o extrato da contabilidade`,
              ]
                .filter(Boolean)
                .join(" e ")}
              . Confira antes de fechar.
            </AvisoContextual>
          )}
          {!calculados && (
            <AvisoContextual tom="alerta" icon={Info}>
              Estes holerites vieram do extrato da contabilidade (competência anterior ao cálculo pelo sistema).
            </AvisoContextual>
          )}
          <label className="relative flex max-w-sm items-center">
            <Search className="pointer-events-none absolute left-3 h-4 w-4 text-muted-foreground" />
            <Input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por nome ou cargo"
              className="pl-9"
            />
          </label>
          <div className="overflow-hidden rounded-xl border bg-card">
            <Table className="min-w-[1000px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-5">Empregado</TableHead>
                  <TableHead>Cargo</TableHead>
                  <TableHead className="text-right">Proventos</TableHead>
                  <TableHead className="text-right">Descontos</TableHead>
                  {mostrarAjuste && <TableHead className="text-right">Ajuste</TableHead>}
                  <TableHead className="text-right">Líquido</TableHead>
                  <TableHead className="text-right">FGTS</TableHead>
                  {comConferencia && <TableHead className="pr-5 text-right">Contabilidade</TableHead>}
                  {editavel && mostrarAjuste && <TableHead className="w-24" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {visiveis.map((h) => {
                  const diferenca = diferencaContabilidade(h);
                  const naoBate = divergeDaContabilidade(h);
                  return (
                    <TableRow
                      key={h.id}
                      className={`cursor-pointer ${h.divergente || naoBate ? "bg-destructive/[.04]" : ""}`}
                      onClick={() => void abrir(h)}
                    >
                      <TableCell className="max-w-[320px] pl-5">
                        <div className="flex items-center gap-1.5 font-medium">
                          {carregando === h.id && <Loader2 className="h-3 w-3 animate-spin" />}
                          {h.divergente && <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-destructive" />}
                          <span className="truncate">{h.nome}</span>
                        </div>
                        <div className="mt-0.5 flex flex-wrap gap-1">
                          {h.tipo !== "mensal" && (
                            <Badge variant="secondary" className="text-[10px]">
                              {ROTULO_TIPO_HOLERITE[h.tipo]}
                            </Badge>
                          )}
                          {h.situacao && !["Trabalhando", "Pendente"].includes(h.situacao) && (
                            <Badge variant="outline" className="text-[10px]">
                              {h.situacao}
                            </Badge>
                          )}
                          {!h.colaborador && (
                            <Badge variant="destructive" className="text-[10px]">
                              Sem cadastro associado
                            </Badge>
                          )}
                        </div>
                        {h.divergente && h.divergencias && (
                          <p className="mt-1 whitespace-pre-line text-xs text-destructive">{h.divergencias}</p>
                        )}
                        {h.avisos && (
                          <p className="mt-1 flex items-start gap-1 text-xs text-muted-foreground">
                            <Info className="mt-0.5 h-3 w-3 shrink-0" />
                            <span className="line-clamp-2">{h.avisos}</span>
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{h.cargo ?? "—"}</TableCell>
                      <TableCell className="text-right">{formatCents(h.proventos)}</TableCell>
                      <TableCell className="text-right text-destructive">
                        {h.descontos === 0 ? formatCents(0) : `− ${formatCents(h.descontos)}`}
                      </TableCell>
                      {mostrarAjuste && (
                        <TableCell className="text-right">
                          {!h.ajusteManual && !h.outrosDescontos ? (
                            <span className="text-muted-foreground">—</span>
                          ) : (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="cursor-help tabular-nums underline decoration-dotted underline-offset-2">
                                  {formatCents((h.ajusteManual ?? 0) - (h.outrosDescontos ?? 0))}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                {!!h.ajusteManual && (
                                  <p>
                                    Ajuste {formatCents(h.ajusteManual)}: {h.ajusteMotivo ?? "sem motivo informado"}
                                  </p>
                                )}
                                {!!h.outrosDescontos && <p>Outros descontos − {formatCents(h.outrosDescontos)}</p>}
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </TableCell>
                      )}
                      <TableCell className="text-right font-semibold">{formatCents(h.liquido)}</TableCell>
                      <TableCell className="text-right text-muted-foreground">{formatCents(h.valorFgts)}</TableCell>
                      {comConferencia && (
                        <TableCell className="pr-5 text-right">
                          {h.liquidoContabilidade === null || diferenca === null ? (
                            <span className="text-muted-foreground">—</span>
                          ) : (
                            <>
                              <div
                                className={`font-semibold ${
                                  naoBate
                                    ? "text-destructive"
                                    : diferenca !== 0
                                      ? "text-amber-700 dark:text-amber-400"
                                      : "text-success"
                                }`}
                              >
                                {naoBate ? "Divergente" : "Confere"}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {formatCents(h.liquidoContabilidade)}
                                {diferenca !== 0 && (
                                  <>
                                    {" · "}
                                    {diferenca > 0 ? "+" : ""}
                                    {formatCents(diferenca)}
                                  </>
                                )}
                              </div>
                            </>
                          )}
                        </TableCell>
                      )}
                      {editavel && mostrarAjuste && (
                        <TableCell className="pr-4 text-right">
                          {h.ajustavel && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 text-[13px] text-primary-text hover:text-primary-text"
                              onClick={(e) => {
                                e.stopPropagation(); // a linha abre o holerite
                                abrirAjuste(h);
                              }}
                              aria-label={`Ajustar holerite de ${h.nome}`}
                            >
                              Ajustar
                            </Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={2} className="pl-5">
                    {visiveis.length} documento(s)
                  </TableCell>
                  <TableCell className="text-right">{formatCents(soma((h) => h.proventos))}</TableCell>
                  <TableCell className="text-right">{formatCents(soma((h) => h.descontos))}</TableCell>
                  {mostrarAjuste && (
                    <TableCell className="text-right">
                      {formatCents(soma((h) => (h.ajusteManual ?? 0) - (h.outrosDescontos ?? 0)))}
                    </TableCell>
                  )}
                  <TableCell className="text-right">{formatCents(soma((h) => h.liquido))}</TableCell>
                  <TableCell className="text-right">{formatCents(soma((h) => h.valorFgts))}</TableCell>
                  {comConferencia && (
                    <TableCell className="pr-5 text-right">
                      {formatCents(soma((h) => h.liquidoContabilidade ?? 0))}
                    </TableCell>
                  )}
                  {editavel && mostrarAjuste && <TableCell />}
                </TableRow>
              </TableFooter>
            </Table>
          </div>
        </>
      )}

      <Dialog open={!!aberto} onOpenChange={(v) => !v && setAberto(null)}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Holerite</DialogTitle>
          </DialogHeader>
          {aberto && <HoleriteCard holerite={aberto} imprimivel mostrarAvisos />}
        </DialogContent>
      </Dialog>

      {ajustando && (
        <AjustarItemModal
          open={!!ajustando}
          onClose={() => setAjustando(null)}
          folhaId={folhaId}
          item={ajustando}
          endpoint={`/clt/folhas/${folhaId}/ajustes/${ajustando.colaboradorId}`}
          metodo="put"
          nota={
            <>
              No CLT o ajuste vira rubrica no holerite mensal (provento se positivo, desconto se
              negativo), <strong>sem INSS, IRRF nem FGTS</strong> — mexe só no líquido, como no PJ.
            </>
          }
          onSalvo={onChange}
        />
      )}
    </div>
  );
};
